import { useSyncExternalStore } from 'react';

/**
 * Día or Noche for the Helios-structured looks (Dale, Moderno and Futurístico), as
 * the Helios CRM does with its moon button. Saved per device, applied before
 * the first paint (main.tsx). Vintage ignores it: its CSS never reads the tema.
 */

export const TEMAS = ['dia', 'noche'] as const;
export type Tema = (typeof TEMAS)[number];
export const TEMA_STORAGE_KEY = 'dale.crm.tema';

type TemaStorage = Pick<Storage, 'getItem' | 'setItem'>;

export function readTema(storage: TemaStorage | undefined): Tema {
  try {
    return storage?.getItem(TEMA_STORAGE_KEY) === 'noche' ? 'noche' : 'dia';
  } catch {
    return 'dia';
  }
}

function browserStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

const listeners = new Set<() => void>();
let current: Tema = typeof window === 'undefined' ? 'dia' : readTema(browserStorage());

export function applyTema(tema: Tema = current): void {
  document.documentElement.dataset.tema = tema;
}

export function setTema(tema: Tema): void {
  current = tema;
  try {
    browserStorage()?.setItem(TEMA_STORAGE_KEY, tema);
  } catch {
    // Private windows: the choice lasts only this visit.
  }
  applyTema(tema);
  listeners.forEach((listener) => listener());
}

export function useTema(): Tema {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    () => current,
    () => current,
  );
}
